import { useQuery, useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useEffect, useRef, useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import {
  isPersonalChatTidyable,
  lastPersonalChatMessageAt,
} from "../../../shared/personalChatInactivity";
import { queries } from "../../../shared/queries";
import { config } from "../../environment";
import { useAdminChatView } from "../../hooks/useAdminChatView";
import { useShiftOptionShortcut } from "../../hooks/useShiftOptionShortcut";
import { downloadChatAsJson } from "../../lib/downloadChat";
import { chatExportPath } from "../../lib/apiPaths";
import { readDraft, restoreDraft } from "../../lib/draft";
import { navigate, routes, useRouteMatch } from "../../lib/urls";
import { Backdrop } from "../Backdrop";
import { Spinner } from "../Spinner";
import { useToast } from "../Toast";
import { PersonalChatNavigation } from "./PersonalChatNavigation";
import { nextChatIdAfterDelete } from "./nextChatIdAfterDelete";
import { resolveActiveTab } from "./resolveActiveTab";
import { usePersonalChatContent } from "./usePersonalChatContent";
import { ChatWorkspace } from "./ChatWorkspace";
import { useChatWorkflowUrl } from "../../lib/chatWorkflowUrl";
import { chatLabel } from "./pickChatIconColor";
import { groupWorkflowChats } from "./groupWorkflowChats";
import {
  forgetHandedOffPersonalChats,
  handedOffPendingPersonalChats,
  type PendingPersonalChat,
} from "../../lib/pendingPersonalChats";
import { ConfirmModal } from "../ConfirmModal";
import { RenamePersonalChatModal } from "./RenamePersonalChatModal";
import { RecentlyDeletedChatsModal } from "./RecentlyDeletedChatsModal";
import { TidyUpChatsModal } from "./TidyUpChatsModal";
import { tidyUpRows } from "./tidyUpRows";
import { ChatPageSurface } from "./ChatPageSurface";
import {
  WorkflowBackRowSkeleton,
  WorkflowNavigationSkeleton,
} from "../WorkflowNavigation";

type Pending = PendingPersonalChat;

const hasChatDraft = (id: string) =>
  !!(readDraft(`/chat:${id}`) || readDraft(`/chat:${id}:monitoring`));

export function PersonalChats() {
  const z = useZero();
  const [onChat, chatMatch] = useRouteMatch(routes.chats);
  const [onSpaceWorkflow, spaceWorkflowMatch] = useRouteMatch(
    routes.space.workflow
  );
  const [onPersonalWorkflow, personalWorkflowMatch] = useRouteMatch(
    routes.personal.workflow
  );
  const workflowMatch = onSpaceWorkflow
    ? spaceWorkflowMatch
    : onPersonalWorkflow
      ? personalWorkflowMatch
      : null;
  const tabId = onChat ? chatMatch.tabId : workflowMatch?.chatId;
  const prompt = chatMatch.q;
  const focus = workflowMatch
    ? {
        spaceId: workflowMatch.spaceId ?? "",
        workflowId: workflowMatch.workflowId!,
        step: workflowMatch.step,
      }
    : null;
  const { isAdminView, userId } = useAdminChatView();
  const chatOwnerId = userId ?? z.userID!;
  const navigation = workflowMatch?.navigation;
  const showWorkflowNavigation =
    !!focus &&
    !isAdminView &&
    (navigation === "workflow" || navigation === "source");
  const [workflowNavigationSlot, setWorkflowNavigationSlot] =
    useState<HTMLDivElement | null>(null);
  const [workflowHeaderSlot, setWorkflowHeaderSlot] =
    useState<HTMLDivElement | null>(null);
  const { showToast, hideToast } = useToast();

  const [tabs, tabsResult] = useQuery(
    queries.personalChatSummaries({
      ...(isAdminView ? { userId } : {}),
      includeClosed: true,
    })
  );
  const [pendings, setPendings] = useState<Pending[]>(
    handedOffPendingPersonalChats
  );
  const [renamingChat, setRenamingChat] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deletingChat, setDeletingChat] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deletionPending, setDeletionPending] = useState(false);
  const [recentlyDeletedOpen, setRecentlyDeletedOpen] = useState(false);
  const [tidyUpOpen, setTidyUpOpen] = useState(false);

  const realTab = tabs.find((t) => t.id === tabId);

  // Drop pendings once their real tab has landed in the synced list.
  const prunedPendings = pendings.filter(
    (p) => !tabs.some((t) => t.id === p.id)
  );
  if (prunedPendings.length !== pendings.length) {
    forgetHandedOffPersonalChats(
      pendings.filter((p) => !prunedPendings.includes(p)).map((p) => p.id)
    );
    setPendings(prunedPendings);
  }

  const activePending = pendings.find((p) => p.id === tabId) ?? null;

  const activeName = realTab?.name ?? (activePending ? "Untitled" : undefined);
  const tabTitle =
    activeName === undefined
      ? config.title
      : `${chatLabel(activeName)} • ${config.title}`;

  useEffect(() => {
    if (!onChat) return;
    if (tabId && deletingChat?.id === tabId) return;
    const action =
      prompt?.trim() && !isAdminView && tabsResult.type === "complete"
        ? { type: "create" as const }
        : resolveActiveTab({
            tabId,
            tabs,
            pendings,
            isAdminView,
            tabsLoaded: tabsResult.type === "complete",
            hasDraft: hasChatDraft,
          });
    if (action.type === "stay") {
      if (prompt !== undefined && (isAdminView || !prompt.trim()))
        navigate(routes.chats, { q: "" }, { replace: true });
      return;
    }
    if (action.type === "navigate") {
      navigate(
        routes.chats,
        { tabId: action.tabId, userId, q: "" },
        { replace: true }
      );
      return;
    }
    const next: Pending = { id: nanoid(12), chatId: nanoid(12) };
    if (prompt?.trim()) restoreDraft(`/chat:${next.id}`, prompt);
    z.mutate(
      sharedMutators.createPersonalChat({ id: next.id, chatId: next.chatId })
    );
    // oxlint-disable-next-line react/set-state-in-effect -- EffectSetState: tracks the optimistic pending chat alongside the createPersonalChat mutation and navigation this effect drives
    setPendings((previous) => [next, ...previous]);
    navigate(
      routes.chats,
      { tabId: next.id, userId, q: "" },
      { replace: true }
    );
  }, [
    onChat,
    tabs,
    pendings,
    isAdminView,
    userId,
    tabsResult.type,
    tabId,
    prompt,
    z,
    deletingChat?.id,
  ]);

  const unknownFocusChat =
    !!focus &&
    tabsResult.type === "complete" &&
    !tabs.some((t) => t.id === tabId) &&
    !pendings.some((p) => p.id === tabId);
  useEffect(() => {
    if (unknownFocusChat) navigate(null, { chatId: "" }, { replace: true });
  }, [unknownFocusChat]);

  const chatWorkflowUrl = useChatWorkflowUrl();
  const attachedWorkflow = realTab?.workflow;
  const attachedBranchId = realTab?.branchId;
  const realTabId = realTab?.id;
  // Only arriving at a chat opens its workflow, so detaching in place stays on the chat.
  const arrivedTabRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!realTabId || arrivedTabRef.current === realTabId) return;
    arrivedTabRef.current = realTabId;
    if (!onChat || isAdminView || !attachedWorkflow || prompt?.trim()) return;
    history.replaceState(
      history.state,
      "",
      chatWorkflowUrl(realTabId, {
        id: attachedWorkflow.id,
        spaceId: attachedWorkflow.spaceId,
        branchId: attachedBranchId,
      })
    );
  }, [
    onChat,
    isAdminView,
    realTabId,
    attachedWorkflow,
    attachedBranchId,
    chatWorkflowUrl,
    prompt,
  ]);

  const newTab = () => {
    const action = resolveActiveTab({
      tabId: undefined,
      tabs,
      pendings,
      isAdminView,
      tabsLoaded: tabsResult.type === "complete",
      hasDraft: hasChatDraft,
    });
    if (action.type === "stay") return;
    if (action.type === "navigate") {
      navigate(routes.chats, { tabId: action.tabId });
      return;
    }
    const next: Pending = { id: nanoid(12), chatId: nanoid(12) };
    z.mutate(
      sharedMutators.createPersonalChat({ id: next.id, chatId: next.chatId })
    );
    setPendings((prev) => [next, ...prev]);
    navigate(routes.chats, { tabId: next.id });
  };
  useShiftOptionShortcut("KeyN", newTab, {
    enabled: onChat && !isAdminView,
    allowInEditable: true,
  });

  const deleteChat = async () => {
    if (!deletingChat || isAdminView || deletionPending) return;
    const { id } = deletingChat;
    setDeletionPending(true);
    const failed = () => {
      setDeletionPending(false);
      showToast({
        icon: "circle-off",
        message: "Failed to delete chat",
        buttonLabel: "Dismiss",
      });
    };
    try {
      const result = await z.mutate(sharedMutators.deletePersonalChat({ id }))
        .server;
      if (result.type === "error") {
        failed();
        return;
      }
    } catch {
      failed();
      return;
    }
    setPendings((previous) => previous.filter((pending) => pending.id !== id));
    setDeletionPending(false);
    setDeletingChat(null);
    if (id === tabId) {
      const nextId = nextChatIdAfterDelete({
        tabs,
        pendings,
        deletedIds: [id],
      });
      navigate(
        routes.chats,
        { tabId: nextId ?? "", userId },
        { replace: true }
      );
    }
  };

  const sortedTabs = [...tabs].sort((a, b) => {
    const aPinned = a.pinnedAt != null;
    const bPinned = b.pinnedAt != null;
    if (aPinned !== bPinned) return aPinned ? -1 : 1;
    return (a.position ?? a.createdAt) - (b.position ?? b.createdAt);
  });
  const realTabItems = sortedTabs.map((t) => ({
    id: t.id,
    name: t.name,
    lastActivityAt: Math.max(
      lastPersonalChatMessageAt(t.chat?.entries, t.createdAt),
      t.workflowOpenedAt ?? 0
    ),
    lastActiveAt: t.lastActiveAt,
    pinned: t.pinnedAt != null,
    position: t.position ?? t.createdAt,
    pending: false,
    started: t.chat?.lastChatEntryType != null,
    responding: t.chat?.respondingAt != null,
    awaitingAction: !!t.chat?.awaitingAction,
    workflow: t.workflow && {
      ...t.workflow,
      branchId: t.branchId ?? undefined,
      branchName: t.branch?.name,
    },
  }));
  const tabsForBar = isAdminView
    ? realTabItems
    : [
        ...realTabItems,
        ...pendings
          .filter((p) => !tabs.some((t) => t.id === p.id))
          .map((p) => ({
            id: p.id,
            name: "Untitled",
            lastActivityAt: null,
            pinned: false,
            position: Number.MAX_SAFE_INTEGER,
            pending: true,
            started: false,
          })),
      ];

  const tidyRows = isAdminView
    ? []
    : tidyUpRows(
        tabs,
        (t) => isPersonalChatTidyable(t, tabId) && !hasChatDraft(t.id)
      ).map((row) => ({
        chat: realTabItems.find((item) => item.id === row.chat.id)!,
        chatIds: row.chatIds,
      }));

  const activeTabId = isAdminView
    ? (realTab?.id ?? null)
    : (realTab?.id ?? activePending?.id ?? null);

  const emptyChat =
    tabsResult.type === "complete" &&
    realTab?.chat &&
    realTab.chat.lastChatEntryType == null
      ? realTab.chat
      : !isAdminView && activePending
        ? {
            id: activePending.chatId,
            respondingAt: null,
            lastLoopHeartbeatAt: null,
            awaitingAction: null,
          }
        : undefined;
  const activeChat = usePersonalChatContent(tabId, emptyChat, chatOwnerId);
  const workspace =
    activeTabId && activeChat
      ? { id: activeTabId, chat: activeChat, pending: !realTab }
      : null;

  const renameChat = (id: string) => {
    const chat = tabs.find((t) => t.id === id);
    if (chat) setRenamingChat({ id: chat.id, name: chat.name });
  };

  const forkChat = async (id: string) => {
    const toastKey = nanoid(12);
    showToast({
      key: toastKey,
      icon: <Spinner />,
      message: "Loading chat to fork…",
      buttonLabel: "Dismiss",
      persist: true,
    });
    let tab;
    try {
      tab = await z
        .run(
          queries.loadPersonalChat({ personalChatId: id, userId: chatOwnerId }),
          {
            type: "complete",
          }
        )
        .finally(() => hideToast(toastKey));
    } catch {
      showToast({
        icon: "circle-off",
        message: "Failed to fork chat",
        buttonLabel: "Dismiss",
      });
      return;
    }
    if (!tab?.chat) return;
    const entryIds: Record<string, string> = {};
    const attachmentIds: Record<string, string> = {};
    for (const entry of tab.chat.entries) {
      if (entry.streaming) continue;
      entryIds[entry.id] = nanoid(12);
      for (const attachment of entry.attachments) {
        attachmentIds[attachment.id] = nanoid(12);
      }
    }
    const newId = nanoid(12);
    z.mutate(
      sharedMutators.forkPersonalChat({
        id: newId,
        chatId: nanoid(12),
        sourceChatId: tab.chat.id,
        entryIds,
        attachmentIds,
      })
    );
    navigate(routes.chats, { tabId: newId, userId });
  };

  const downloadChat = async (id: string) => {
    const toastKey = nanoid(12);
    showToast({
      key: toastKey,
      icon: <Spinner />,
      message: "Preparing chat download…",
      buttonLabel: "Dismiss",
      persist: true,
    });
    try {
      await downloadChatAsJson(
        chatExportPath("personal", id),
        chatLabel(tabs.find((tab) => tab.id === id)!.name)
      ).finally(() => hideToast(toastKey));
      z.mutate(sharedMutators.exportPersonalChat({ id }));
    } catch {
      showToast({
        icon: "circle-off",
        message: "Failed to download chat",
        buttonLabel: "Dismiss",
      });
    }
  };

  const togglePin = (id: string) => {
    const tab = tabs.find((t) => t.id === id);
    if (!tab) return;
    if (tab.pinnedAt != null) {
      z.mutate(
        sharedMutators.setPersonalChatPinned({ id: tab.id, pinned: false })
      );
      return;
    }
    const minPosition = Math.min(...tabs.map((t) => t.position ?? t.createdAt));
    z.mutate(
      sharedMutators.setPersonalChatPinned({
        id: tab.id,
        pinned: true,
        position: minPosition - 1000,
      })
    );
  };

  const chatNavigation = (
    <PersonalChatNavigation
      chats={groupWorkflowChats(tabsForBar, activeTabId)}
      activeChatId={activeTabId}
      userId={userId}
      onDelete={
        isAdminView
          ? undefined
          : (id) => {
              const chat = tabsForBar.find((chat) => chat.id === id);
              if (chat)
                setDeletingChat({ id: chat.id, name: chatLabel(chat.name) });
            }
      }
      onRename={isAdminView ? undefined : renameChat}
      onFork={isAdminView ? undefined : forkChat}
      onDownload={isAdminView ? undefined : downloadChat}
      onTogglePin={isAdminView ? undefined : togglePin}
      onNewChat={isAdminView ? undefined : newTab}
      onRecentlyDeleted={
        isAdminView ? undefined : () => setRecentlyDeletedOpen(true)
      }
      onTidyUp={tidyRows.length > 0 ? () => setTidyUpOpen(true) : undefined}
      readOnly={isAdminView}
    />
  );

  return (
    <>
      {!focus && <title>{tabTitle}</title>}
      <Backdrop
        permanentNavigation
        inChat
        headerContent={
          !showWorkflowNavigation ? undefined : workspace ? (
            <div ref={setWorkflowHeaderSlot} style={{ display: "contents" }} />
          ) : (
            <WorkflowBackRowSkeleton />
          )
        }
        navigation={
          !showWorkflowNavigation ? (
            chatNavigation
          ) : workspace ? (
            <div
              ref={setWorkflowNavigationSlot}
              style={{ display: "contents" }}
            />
          ) : (
            <WorkflowNavigationSkeleton />
          )
        }
      >
        <ChatPageSurface>
          {recentlyDeletedOpen && !isAdminView && (
            <RecentlyDeletedChatsModal
              onClose={() => setRecentlyDeletedOpen(false)}
            />
          )}
          <TidyUpChatsModal
            open={tidyUpOpen}
            onClose={() => setTidyUpOpen(false)}
            rows={tidyRows}
          />
          {renamingChat && !isAdminView && (
            <RenamePersonalChatModal
              initialName={renamingChat.name}
              onClose={() => setRenamingChat(null)}
              onRename={(name) => {
                z.mutate(
                  sharedMutators.renamePersonalChat({
                    id: renamingChat.id,
                    name,
                  })
                );
              }}
            />
          )}
          <ConfirmModal
            open={deletingChat !== null}
            pending={deletionPending}
            onClose={() => setDeletingChat(null)}
            onConfirm={deleteChat}
            title="Delete chat"
            description={
              <>
                Delete “{deletingChat?.name}” from your chat history? You can
                restore it from Deleted chats.
              </>
            }
            confirmLabel="Delete chat"
            destructive
          />
          {workspace && (
            <ChatWorkspace
              key={workspace.id}
              personalChatId={workspace.id}
              chatOwnerId={chatOwnerId}
              chat={workspace.chat}
              pending={workspace.pending}
              readOnly={isAdminView}
              focus={focus}
              workflowNavigationSlot={
                showWorkflowNavigation ? workflowNavigationSlot : null
              }
              workflowHeaderSlot={
                showWorkflowNavigation ? workflowHeaderSlot : null
              }
              // A deleted workflow takes its chat out of the list; a workflow you can no longer read leaves it as a plain chat.
              onWorkflowGone={() =>
                navigate(
                  routes.chats,
                  {
                    tabId: tabs.some((tab) => tab.id === workspace.id)
                      ? workspace.id
                      : "",
                  },
                  { replace: true }
                )
              }
            />
          )}
        </ChatPageSurface>
      </Backdrop>
    </>
  );
}
